'use client';

import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { DataTestConfig, PRODUCT_NOTIFY_ME } from '@headless-workspace/config';
import { ContentAssetKeys, unwrapResultSuccess } from '@headless-workspace/core-domain';
import { useToasts, useModalActions } from '@headless-workspace/core-ui';
import {
    GlowButton,
    GlowIcon,
    GlowModal,
    GlowSkeleton,
    GlowTextBody,
    Icons,
    PropsWithLocale,
} from '@headless-workspace/glow-ds';
import { Component } from '@headless-workspace/typings';
import { getConnectedUserInfo, HTMLString } from '../../../server';
import { InnerHTML } from '../innerHTML';
import { useUserInfo } from '../user';
import { NotifyMeForm } from './NotifyMeForm';
import { useNotifyMeAssets } from './useNotifyMeAssets';

type NotifyMeFormProps = PropsWithLocale & {
    productIds: string[];
};

export const NotifyMeModal: Component<NotifyMeFormProps> = ({ productIds, locale }) => {
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
    const t = useTranslations('Discover.subscribe.notifyMe');
    const { result: userInfoResult } = useUserInfo();
    const userInfo = getConnectedUserInfo(userInfoResult);
    const { notifyError } = useToasts();

    const { close: closeMenu } = useModalActions();

    const closeNotifyMeModal = useCallback(() => {
        closeMenu(PRODUCT_NOTIFY_ME, true);
    }, [closeMenu]);

    useEffect(() => {
        setIsSubmitted(false);
    }, [productIds]);

    const { isLoading, result: notifyMeAssetsResult } = useNotifyMeAssets();
    const notifyMeContentAssets = notifyMeAssetsResult ? unwrapResultSuccess(notifyMeAssetsResult) : undefined;

    useEffect(() => {
        if (notifyMeAssetsResult.type === 'failure') {
            notifyError(notifyMeAssetsResult.meta.message);
        }
    }, [notifyMeAssetsResult, notifyError]);

    if (!notifyMeContentAssets) {
        return null;
    }

    const notifyMeAssets: { [key: string]: HTMLString } | undefined = notifyMeContentAssets.reduce(
        (acc, asset) => Object.assign(acc, { [asset.id]: asset.content.html }),
        {},
    );

    const notifyMeTexts = notifyMeAssets
        ? {
              header: notifyMeAssets[ContentAssetKeys.NotifyMeHeaderText],
              chartProtect: notifyMeAssets[ContentAssetKeys.NotifyMeChartProtectText],
              confirmation: notifyMeAssets[ContentAssetKeys.NotifyMeConfirmationText],
          }
        : undefined;

    return (
        <GlowModal.Container id={PRODUCT_NOTIFY_ME} title={t('modal.label.title')} onClick={closeNotifyMeModal}>
            <NotifyMeModalContent
                productIds={productIds}
                locale={locale}
                isLoading={isLoading}
                isSubmitted={isSubmitted}
                setIsSubmitted={setIsSubmitted}
                notifyMeTexts={notifyMeTexts}
                userEmail={userInfo?.email}
                closeModal={{
                    label: t('modal.action.close'),
                    action: closeNotifyMeModal,
                }}
            />
        </GlowModal.Container>
    );
};

type NotifyMeModalContentProps = NotifyMeFormProps & {
    isLoading: boolean;
    isSubmitted: boolean;
    setIsSubmitted: Dispatch<SetStateAction<boolean>>;
    notifyMeTexts?: {
        header: HTMLString;
        chartProtect: HTMLString;
        confirmation: HTMLString;
    };
    userEmail?: string;
    closeModal: {
        label: string;
        action: () => void;
    };
};

const NotifyMeModalContent: Component<NotifyMeModalContentProps> = ({
    productIds,
    locale,
    isLoading,
    isSubmitted,
    setIsSubmitted,
    notifyMeTexts,
    userEmail,
    closeModal,
}) => {
    if (isLoading) {
        return (
            <div className={'flex flex-col gap-2 m-1'}>
                <GlowSkeleton height={'4rem'} />
                <GlowSkeleton height={'3rem'} />
                <GlowSkeleton height={'12rem'} />
            </div>
        );
    }

    if (!isSubmitted) {
        return (
            <NotifyMeForm
                onSubmit={setIsSubmitted}
                productIds={productIds}
                header={notifyMeTexts?.header}
                chartProtectText={notifyMeTexts?.chartProtect}
                locale={locale}
                defaultEmail={userEmail}
                onCloseModal={closeModal.action}
            />
        );
    }

    return (
        <>
            <GlowModal.Content dataTestId={DataTestConfig.testIds.notifyMeConfirmMessage}>
                <GlowIcon Icon={Icons.NotifyMe} type={'xxLarge'} />
                <GlowTextBody innerHTML={<InnerHTML rawHtml={notifyMeTexts?.confirmation} />} />
            </GlowModal.Content>
            <GlowModal.Footer>
                <GlowButton label={closeModal.label} variant={'primary'} onClick={closeModal.action} width={'full'} />
            </GlowModal.Footer>
        </>
    );
};
